import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  Dispatch,
  SetStateAction,
  useCallback,
} from 'react'

import { FirebaseError } from 'firebase/app'
import {
  Auth,
  signInWithPopup,
  createUserWithEmailAndPassword as createUserWithEmailAndPasswordFunc,
  signInWithEmailAndPassword as signInWithEmailAndPasswordFunc,
  UserCredential,
  GoogleAuthProvider,
  FacebookAuthProvider,
  updatePassword,
  updateEmail,
  User,
  sendEmailVerification,
  sendPasswordResetEmail,
} from 'firebase/auth'
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  setDoc,
} from 'firebase/firestore'

import { auth, firestore } from '../firebase/firebaseConfig'
import { updateStudentLoginLog } from '../firebase/utils'
import { deleteCollection } from '../firebase/utils/collectionActions'
import { updateDocument } from '../firebase/utils/documentsActions'
import { updateAList } from '../firebase/utils/lists'
import { getCookie, removeCookie, setCookie } from '../hooks/cookies'
import { Person, ProfileRole } from '../types'
import { convertToRoles, convertToSpanish } from '../utilFunctions'
import { handleErrors } from '../utilFunctions/firebaseErrors'

interface UserContextProps {
  currentUser: User | null
  user: Person | null
  userRole: ProfileRole | null
  auth: Auth
  onEditionMode: boolean
  loadingUser: boolean
  createUserWithEmailAndPassword: (
    email: string,
    password: string,
  ) => Promise<UserCredential | null>
  signInWithGoogle: (userType: ProfileRole) => void
  signInWithEmailAndPassword: (
    email: string,
    password: string,
    userType: ProfileRole,
  ) => Promise<string | null>
  signInWithFacebook: (userType: ProfileRole) => void
  changePassword: (
    currentPassword: string,
    newPassword: string,
  ) => Promise<void>
  signOut: () => void
  setOnEditionMode: Dispatch<SetStateAction<boolean>>
  updateUser: (userData: Person) => Promise<string | null>
  changeEmail: (newEmail: string, userRole: ProfileRole) => Promise<void>
  deleteUser: (userId: string, userRole: ProfileRole) => Promise<string | null>
  sendPasswordReset: (email: string) => Promise<string | null>
  initDone: boolean
}

const UserContext = createContext<UserContextProps>({
  currentUser: null,
  user: null,
  userRole: null,
  auth: auth,
  onEditionMode: false,
  loadingUser: false,
  createUserWithEmailAndPassword: () => Promise.resolve(null),
  signInWithGoogle: () => Promise.resolve(),
  signInWithEmailAndPassword: () => Promise.resolve(null),
  signInWithFacebook: () => Promise.resolve(),
  changePassword: () => Promise.resolve(),
  signOut: () => Promise.resolve(),
  setOnEditionMode: () => Promise.resolve(false),
  updateUser: () => Promise.resolve(null),
  changeEmail: () => Promise.resolve(),
  deleteUser: () => Promise.resolve(null),
  sendPasswordReset: () => Promise.resolve(''),
  initDone: false,
})

export const useUser = () => useContext(UserContext)

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null)
  const [user, setUser] = useState<Person | null>(null)
  const [userRole, setUserRole] = useState<ProfileRole | null>(null)
  const [onEditionMode, setOnEditionMode] = useState<boolean>(false)
  const [loadingUser, setLoadingUser] = useState(false)
  const [initDone, setInitDone] = useState(false)
  // TODO: add loading and error
  // const [userError, setUserError] = useState<string | null>(null)

  const fetchUserData = async (user: User, role: ProfileRole) => {
    if (!role) return
    const userRef = doc(firestore, convertToRoles(role), user.uid)
    const unsubscribeUser = onSnapshot(userRef, (snapshot) => {
      if (snapshot.exists()) {
        setUser(snapshot.data() as Person)
      }
      // No establecer null aquí - dejar que handleStudentLoginSideEffects establezca el usuario
    })
    return unsubscribeUser
  }

  const handleStudentLoginSideEffects = async (
    userData: Person,
    uid: string,
    rememberRole: string,
  ) => {
    setUser(userData)
    setCookie('userRole', rememberRole, {
      expires: 30,
      secure: true,
      sameSite: 'strict',
    })
    const hasForm = userData.form ? 'true' : 'false'
    localStorage.setItem('hasForm', hasForm)
    await updateStudentLoginLog(uid)
  }

  useEffect(() => {
    setLoadingUser(true)

    let unsubscribeUser: (() => void) | undefined

    const unsubscribeAuth = auth.onAuthStateChanged(async (_user) => {
      if (_user) {
        setCurrentUser(_user)
        const role = getCookie('userRole') as ProfileRole
        setUserRole(role || null)

        // Fetch user data
        unsubscribeUser = await fetchUserData(_user, role || null)

        setLoadingUser(false) // Termina la carga cuando los datos están listos
        setInitDone(true)
        // return () => unsubscribeUser()
      } else {
        removeCookie('userRole')
        // removeCookie('userToken')
        setCurrentUser(null)
        setUserRole(null)
        setLoadingUser(false) // Termina la carga cuando no hay usuario
        setInitDone(true)
      }
    })

    return () => {
      unsubscribeUser?.()
      unsubscribeAuth()
    }
  }, [])

  const createUserWithEmailAndPassword = async (
    email: string,
    password: string,
  ) => {
    try {
      return await createUserWithEmailAndPasswordFunc(auth, email, password)
    } catch (error) {
      const { code } = error as FirebaseError
      handleErrors(code)
      return null
    }
  }

  const signInWithPopupProvider = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (provider: any, userType: ProfileRole) => {
      if (!userType) return
      try {
        const result = await signInWithPopup(auth, provider)
        const currentUser = result.user
        // const token = await currentUser.getIdToken()

        if (!currentUser.emailVerified) {
          await sendEmailVerification(currentUser)
          signOut()
          return handleErrors('email/not-verify')
        }

        // setCookie('userToken', token, {
        //   expires: 30,
        //   secure: true,
        //   sameSite: 'strict',
        // })
        setCookie('userRole', userType, {
          expires: 30,
          secure: true,
          sameSite: 'strict',
        })
        // if (userType === 'student') {
        //   const hasForm = user?.form ? 'true' : 'false'
        //   localStorage.setItem('hasForm', hasForm)
        //   updateStudentLoginLog(currentUser.uid)
        // }
        const userDocRef = doc(
          firestore,
          convertToRoles(userType),
          currentUser.uid,
        )
        const userDoc = await getDoc(userDocRef)

        setUserRole(userType)

        let userData: Person

        if (!userDoc.exists()) {
          userData = {
            uid: currentUser.uid,
            name: currentUser.displayName || '',
            lastname: '',
            phone: currentUser.phoneNumber || '',
            birthDate: '',
            email: currentUser.email || '',
            genre: '',
            activities: [],
            experienceType: '',
            image: currentUser.photoURL || '',
            form: false,
            role: userType,
          }
          await setDoc(userDocRef, userData)
        } else {
          userData = userDoc.data() as Person
        }
        console.log(userData, 'userData')
        if (userType === 'student') {
          await handleStudentLoginSideEffects(
            userData,
            currentUser.uid,
            userType,
          )
        } else {
          setUser(userData)
        }

        // if (!userDoc.exists()) {
        //   const userData: Person = {
        //     uid: currentUser.uid,
        //     name: currentUser.displayName || '',
        //     lastname: '',
        //     phone: currentUser.phoneNumber || '',
        //     birthDate: '',
        //     email: currentUser.email || '',
        //     genre: '',
        //     activities: [],
        //     experienceType: '',
        //     image: currentUser.photoURL || '',
        //     form: false,
        //     role: userType,
        //   }
        //   await setDoc(userDocRef, userData)
        //   setUser(userData)
        // } else {
        //   setUser(userData)

        //   //added
        //   const hasForm = userData.form ? 'true' : 'false'
        //   localStorage.setItem('hasForm', hasForm)
        // }

        return null
      } catch (error) {
        const { code } = error as FirebaseError
        return handleErrors(code)
      }
    },
    [],
  )

  const signInWithGoogle = (userType: ProfileRole) => {
    return signInWithPopupProvider(new GoogleAuthProvider(), userType)
  }

  const signInWithFacebook = (userType: ProfileRole) => {
    return signInWithPopupProvider(new FacebookAuthProvider(), userType)
  }

  const signInWithEmailAndPassword = async (
    email: string,
    password: string,
    userType: ProfileRole,
  ) => {
    try {
      const result = await signInWithEmailAndPasswordFunc(auth, email, password)
      const currentUser = result.user

      if (!userType) return null

      //HABILITAR PARA EL GO-LIVE
      // if (!currentUser.emailVerified) {
      //   await sendEmailVerification(currentUser)
      //   signOut()
      //   return handleErrors('email/not-verify')
      // }

      const userDocRef = doc(
        firestore,
        convertToRoles(userType),
        currentUser.uid,
      )
      const userDoc = await getDoc(userDocRef)

      if (userDoc.exists()) {
        // const token = await currentUser.getIdToken()
        const userData = userDoc.data() as Person

        setUser(userData)
        // setCookie('userToken', token, {
        //   expires: 30,
        //   secure: true,
        //   sameSite: 'strict',
        // })
        setCookie('userRole', userType, {
          expires: 30,
          secure: true,
          sameSite: 'strict',
        })
        setUserRole(userType)

        // if (userType === 'student') {
        //   // const hasForm = user?.form ? 'true' : 'false'
        //   const hasForm = userData.form ? 'true' : 'false'
        //   localStorage.setItem('hasForm', hasForm)
        //   updateStudentLoginLog(currentUser.uid)
        // }
        if (userType === 'student') {
          await handleStudentLoginSideEffects(
            userData,
            currentUser.uid,
            userType,
          )
        } else {
          setUser(userData)
          setCookie('userRole', userType, {
            expires: 30,
            secure: true,
            sameSite: 'strict',
          })
        }
        return null
      } else {
        signOut()
        return `Mmmm... Parece que no perteneces al equipo de ${convertToSpanish(userType)}`
      }
    } catch (error) {
      const { code } = error as FirebaseError
      console.log(code)
      return handleErrors(code)
    }
  }

  const updateUser = async (userData: Person) => {
    if (!currentUser)
      throw new Error('No se ha encontrado un usuario autenticado')
    if (!userData.role) return null

    const documentRef = doc(
      firestore,
      convertToRoles(userData.role),
      userData.uid,
    )

    try {
      await updateDocument(documentRef, userData)
      if (currentUser.uid === userData.uid) {
        setUser(userData) // Actualizar el estado local con los nuevos datos
      }

      return null
    } catch (error) {
      const { code } = error as FirebaseError
      return handleErrors(code)
    }
  }

  const changeEmail = async (newEmail: string, userRole: ProfileRole) => {
    if (!currentUser)
      throw new Error('No se ha encontrado un usuario autenticado')
    if (!userRole) return
    const documentRef = doc(
      firestore,
      convertToRoles(userRole),
      currentUser.uid,
    )

    try {
      await updateEmail(currentUser, newEmail)
      await updateDocument(documentRef, {
        email: newEmail,
      })

      setUser((prevUser) =>
        prevUser ? { ...prevUser, email: newEmail } : null,
      )
    } catch (error) {
      const { code } = error as FirebaseError
      throw handleErrors(code)
    }
  }

  const changePassword = async (
    currentPassword: string,
    newPassword: string,
  ) => {
    if (!currentUser)
      throw new Error('No se ha encontrado un usuario autenticado')

    try {
      const credentials = await signInWithEmailAndPasswordFunc(
        auth,
        currentUser.email || '',
        currentPassword,
      )
      if (credentials) {
        await updatePassword(currentUser, newPassword)
      } else {
        throw new Error('Contraseña actual incorrecta')
      }
    } catch (error) {
      const { code } = error as FirebaseError
      throw handleErrors(code)
    }
  }

  const signOut = () => {
    auth
      .signOut()
      .then(() => {
        // removeCookie('userToken')
        removeCookie('userRole')
        setUserRole(null)
        setUser(null)
        localStorage.removeItem('hasForm')
      })
      .catch((error) => {
        const { code } = error as FirebaseError
        handleErrors(code)
      })
  }

  const sendPasswordReset = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email)
      return null
    } catch (error) {
      const { code } = error as FirebaseError
      throw handleErrors(code)
    }
  }

  const deleteUser = async (userId: string, userRole: string | null) => {
    if (!userRole) return null
    try {
      // Verificar si el usuario existe antes de cualquier operación
      const userToDelete = doc(firestore, userRole, userId)
      const userDoc = await getDoc(userToDelete)

      if (!userDoc.exists()) {
        throw new FirebaseError('not-found-delete', 'User not found')
      }

      if (userRole === 'students') {
        const cuponsCollectionRef = collection(firestore, 'cupons')
        const cuponsSnapshot = await getDocs(cuponsCollectionRef)
        const cuponsToDelete = cuponsSnapshot.docs.filter(
          (doc) => doc.data().idStudent === userId,
        )
        for (const cuponDoc of cuponsToDelete) {
          await deleteDoc(cuponDoc.ref)
        }
        try {
          const studentRef = doc(firestore, userRole, userId)
          const logsCollectionRef = collection(studentRef, 'logs')
          await deleteCollection(logsCollectionRef)
        } catch (error) {
          const { code } = error as FirebaseError
          console.error('Error deleting student logs:', code)
          throw handleErrors(code)
        }
      }

      if (userRole === 'professors') {
        await updateAList('professorsNames', { uid: userId }, 'professors')
        // TODO: replace above line with
        // await professorListRepository.remove(userId)
      }

      // Eliminar el usuario
      try {
        await deleteDoc(userToDelete)
      } catch (error) {
        const { code } = error as FirebaseError
        console.error('Error deleting user document:', code)
        throw handleErrors(code)
      }

      return 'Usuario eliminado de la base de datos.'
    } catch (error) {
      const { code } = error as FirebaseError
      console.error(code, 'error')
      throw handleErrors(code)
    }
  }

  return (
    <UserContext.Provider
      value={{
        currentUser,
        user,
        userRole,
        auth,
        onEditionMode,
        loadingUser,
        createUserWithEmailAndPassword,
        signInWithGoogle,
        signInWithEmailAndPassword,
        signInWithFacebook,
        changePassword,
        signOut,
        setOnEditionMode,
        updateUser,
        changeEmail,
        deleteUser,
        sendPasswordReset,
        initDone,
      }}
    >
      {children}
    </UserContext.Provider>
  )
}
